import { apiGet, apiPost, apiPut } from '../shared/apiClient';
import { identifyDraftParts } from '../drafts/draftEditorModel';
import { buildQueryPartWrite, hasPartSelection } from '../shared/queryPartPayload';
import { imageIdentity } from '../../../../features/query-photos/models/photoTags';
import { SKIP_SUPPLIERS_REASON } from '../../constants/workflowActions';
import { createVehicleFields, draftVehicleFields } from './queryVehicleModel';

export function queryEditorError(error) {
  const body = error.response?.data || error.body || {};
  return Object.assign(new Error(body.message || error.message || 'Query request failed.'), {
    code: body.code, data: body.data, requestId: body.meta?.request_id, status: error.response?.status || error.status,
  });
}
async function request(promise) {
  try {
    const response = await promise;
    if (!response.data?.success) throw Object.assign(new Error(response.data?.message), { body: response.data, status: response.status });
    return response.data.data;
  } catch (error) { throw queryEditorError(error); }
}
const root = '/queries-v1/queries';
export const getDraft = id => request(apiGet(`${root}/${encodeURIComponent(id)}/draft`));
export const createQuery = body => request(apiPost(root, body));
export const saveDraft = (id, body) => request(apiPut(`${root}/${encodeURIComponent(id)}/draft`, body));
export const setCustomerReference = (id, customer_reference, expected) => request(apiPut(`${root}/${encodeURIComponent(id)}/customer-reference`, { customer_reference, expected }));
export function listQueries(params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => { if (key !== 'user_id' && value != null && value !== '') qs.set(key, value); });
  return request(apiGet(`${root}?${qs}`)).then(rows => Array.isArray(rows) ? rows : []);
}
export const partKey = part => String(part.id ?? part.client_key);
export function normalizeCustomerReference(value) {
  const text = String(value ?? '').trim();
  // eslint-disable-next-line no-control-regex -- contract explicitly rejects these control characters
  if (/[\u0000-\u001f\u007f]/.test(String(value ?? '')) || [...text].length > 100) throw Object.assign(new Error('Customer reference must be at most 100 characters and contain no line breaks or control characters.'), { code: 'INVALID_CUSTOMER_REFERENCE' });
  return text || null;
}
export function buildPhotoTags(form, uploadedImages, draft = false) {
  if (form.canManageParts === false) return undefined;
  return (form.images || []).flatMap((image, index) => {
    const keys = new Set((form.tags || {})[imageIdentity(image)] || []);
    const parts = form.parts.filter(part => keys.has(partKey(part)) && hasPartSelection(part));
    if (!parts.length) return [];
    if (parts.length > 10) throw new Error('A photo can show at most 10 parts.');
    return [{ image_id: imageIdentity(uploadedImages[index]), ...(draft
      ? { parts: parts.map(part => part.id != null ? { id: part.id } : { client_key: part.client_key }) }
      : { part_client_keys: parts.map(part => part.client_key) }) }];
  });
}
function fields(form, uploadedImages, draft) {
  const identified = { ...form, parts: identifyDraftParts(form.parts || []) };
  if (identified.parts.some(part => part.id != null && !hasPartSelection(part))) throw new Error('Choose a part name for each saved row, or remove the row before saving.');
  const photo_tags = buildPhotoTags(identified, uploadedImages, draft);
  return {
    urgency: form.urgency, assigned_to: form.assigned_to ?? null,
    remarks: form.remarks?.trim() || null, customer_reference: normalizeCustomerReference(form.customer_reference),
    parts: identified.parts.filter(hasPartSelection).map(part => ({ ...buildQueryPartWrite(part), ...(draft && part.id != null ? { id: part.id } : { client_key: part.client_key }) })),
    images: uploadedImages, ...(photo_tags !== undefined && { photo_tags }),
  };
}
export function buildCreateBody(form, uploadedImages) {
  return { ...fields(form, uploadedImages, false), ...createVehicleFields(form), customer_id: form.customer_id,
    status: form.action === 'skip' ? 'waiting_for_price' : 'draft', ...(form.action === 'skip' && { reason: SKIP_SUPPLIERS_REASON }),
    communication_channel_key: form.communication_channel_key, set_as_customer_default: form.set_as_customer_default === true };
}
export function buildDraftBody(form, uploadedImages, tagVersion, baseline) {
  return { ...fields(form, uploadedImages, true), ...draftVehicleFields(form, baseline), ...(form.canManageParts !== false && { expected_photo_tag_version: tagVersion }) };
}
